<template>
    <div>
        <div class="demo-spin-article">
            <!-- Loan Type -->
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">
                        {{ $t("loanTypeUserDefinedRate.loanType") }}
                    </label>
                    <select
                        v-model="model.loan_type_id"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('loan_type_id') }"
                    >
                        <option :value="null" disabled selected style="display:none">
                            {{ $t("select") }}
                        </option>
                        <option
                            v-for="(lt, i) in loanTypes"
                            :key="i"
                            :value="lt.loan_type_id"
                        >{{ lt.loan_type_en }}</option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.has('loan_type_id')">
                        {{ errors.first("loan_type_id") }}
                    </div>
                </div>
            </div>

            <!-- Interest Rates -->
            <div class="mb-3">
                <label class="form-col-label control-label">
                    {{ $t("loanTypeUserDefinedRate.interestRates") }} (%)
                    <span v-if="loanCurrencyRange" class="badge bg-blue rounded-pill tw-ml-2" style="font-size:11px;font-weight:normal">
                        Min {{ loanCurrencyRange.min_rate }} – Max {{ loanCurrencyRange.max_rate }}
                    </span>
                </label>
                <div class="tag-box" :class="{ 'tag-box-error': irRangeError }">
                    <span v-for="(v, i) in model.interest_rates" :key="i" class="tag-chip chip-blue">
                        {{ v }}<em @mousedown.prevent="removeRate('interest_rates', i)">×</em>
                    </span>
                    <input ref="ir" type="number" step="any" class="tag-field" placeholder="0.00" />
                    <button type="button" class="tag-add-btn" @mousedown.prevent="addRate('interest_rates', 'ir')">+</button>
                </div>
                <small class="text-danger" v-if="irRangeError">{{ irRangeError }}</small>
            </div>

            <!-- Monthly Fees -->
            <div class="mb-3">
                <label class="form-col-label control-label">
                    {{ $t("loanTypeUserDefinedRate.monthlyFees") }} (%)
                </label>
                <div class="tag-box">
                    <span v-for="(v, i) in model.monthly_fees" :key="i" class="tag-chip chip-green">
                        {{ v }}<em @mousedown.prevent="removeRate('monthly_fees', i)">×</em>
                    </span>
                    <input ref="mf" type="number" step="any" class="tag-field" placeholder="0.00" />
                    <button type="button" class="tag-add-btn" @mousedown.prevent="addRate('monthly_fees', 'mf')">+</button>
                </div>
            </div>

            <!-- Admin Fees -->
            <div class="mb-3">
                <label class="form-col-label control-label">
                    {{ $t("loanTypeUserDefinedRate.adminFees") }} (%)
                </label>
                <div class="tag-box">
                    <span v-for="(v, i) in model.admin_fees" :key="i" class="tag-chip chip-gold">
                        {{ v }}<em @mousedown.prevent="removeRate('admin_fees', i)">×</em>
                    </span>
                    <input ref="af" type="number" step="any" class="tag-field" placeholder="0.00" />
                    <button type="button" class="tag-add-btn" @mousedown.prevent="addRate('admin_fees', 'af')">+</button>
                </div>
            </div>

            <!-- Insurance Fees -->
            <div class="mb-3">
                <label class="form-col-label control-label">
                    {{ $t("loanTypeUserDefinedRate.insuranceFees") }} (%)
                </label>
                <div class="tag-box">
                    <span v-for="(v, i) in model.insurance_fees" :key="i" class="tag-chip chip-cyan">
                        {{ v }}<em @mousedown.prevent="removeRate('insurance_fees', i)">×</em>
                    </span>
                    <input ref="inf" type="number" step="any" class="tag-field" placeholder="0.00" />
                    <button type="button" class="tag-add-btn" @mousedown.prevent="addRate('insurance_fees', 'inf')">+</button>
                </div>
            </div>

            <!-- Rescue Fees -->
            <div class="mb-3">
                <label class="form-col-label control-label">
                    {{ $t("loanTypeUserDefinedRate.rescueFees") }}
                </label>
                <div class="tag-box">
                    <span v-for="(v, i) in model.rescue_fees" :key="i" class="tag-chip chip-purple">
                        {{ v }}<em @mousedown.prevent="removeRate('rescue_fees', i)">×</em>
                    </span>
                    <input ref="rf" type="number" step="any" class="tag-field" placeholder="0.00" />
                    <button type="button" class="tag-add-btn" @mousedown.prevent="addRate('rescue_fees', 'rf')">+</button>
                </div>
            </div>

            <div slot="footer">
                <div class="tw-flex tw-justify-end tw-space-x-2">
                    <ts-button @click.prevent="() => $emit('cancel')" class="btn-gray">{{ $t("cancel") }}</ts-button>
                    <ts-button color="primary" outline v-if="!isUpdate" :waiting="waiting_new" :disabled="waiting" @click.prevent="onSaveAddNew">{{ $t("saveAddNew") }}</ts-button>
                    <ts-button color="primary" v-if="!isUpdate" :waiting="waiting" :disabled="waiting_new" @click.prevent="onSave">{{ $t("save") }}</ts-button>
                    <ts-button color="primary" v-if="isUpdate" :waiting="waiting" @click.prevent="onUpdate">{{ $t("update") }}</ts-button>
                </div>
            </div>
        </div>
        <Spin size="large" fix v-if="loading" />
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapActions, mapState } from "vuex";
import { isEmpty } from "lodash";

const RATE_FIELDS = [
    { field: "interest_rates", ref: "ir" },
    { field: "monthly_fees",   ref: "mf" },
    { field: "admin_fees",     ref: "af" },
    { field: "insurance_fees", ref: "inf" },
    { field: "rescue_fees",    ref: "rf" }
];

export default {
    name: "loanTypeUserDefinedRateForm",
    data() {
        return {
            errors: new Errors(),
            loading: false,
            waiting: false,
            waiting_new: false,
            irRangeError: null,
            model: {
                loan_type_id:   null,
                interest_rates: [],
                monthly_fees:   [],
                admin_fees:     [],
                insurance_fees: [],
                rescue_fees:    []
            }
        };
    },
    computed: {
        ...mapState("creditOperation/loanTypeUserDefinedRates", ["edit_data", "loanTypes"]),
        isUpdate() { return !isEmpty(this.edit_data); },
        loanCurrencyRange() {
            if (!this.model.loan_type_id) return null;
            const lt = this.loanTypes.find(l => l.loan_type_id == this.model.loan_type_id);
            if (lt && lt.loan_currency && lt.loan_currency.min_rate != null) {
                return { min_rate: lt.loan_currency.min_rate, max_rate: lt.loan_currency.max_rate };
            }
            return null;
        }
    },
    mounted() {
        this.$nextTick(() => { this._bindEnterKeys(); });
    },
    methods: {
        ...mapActions("creditOperation/loanTypeUserDefinedRates", ["getLoanType"]),
        _bindEnterKeys() {
            RATE_FIELDS.forEach(({ field, ref }) => {
                const el = this.$refs[ref];
                if (!el) return;
                el.addEventListener("keydown", e => {
                    if (e.key === "Enter" || e.keyCode === 13) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        this.addRate(field, ref);
                    }
                }, true); // capture phase — fires before modal handlers
            });
        },
        async fetchResource() {
            this.loading = true;
            await this.getLoanType();
            this.setEditData();
            this.loading = false;
            this.$nextTick(() => { this._bindEnterKeys(); });
        },
        addRate(field, ref) {
            const el = this.$refs[ref];
            if (!el) return;
            const val = parseFloat(el.value);
            if (isNaN(val)) return;
            if (field === 'interest_rates' && this.loanCurrencyRange) {
                const { min_rate, max_rate } = this.loanCurrencyRange;
                if (val < min_rate || val > max_rate) {
                    this.irRangeError = `Rate must be between ${min_rate} and ${max_rate}`;
                    el.select();
                    return;
                }
            }
            this.irRangeError = null;
            if (this.model[field].includes(val)) {
                el.select();
                return;
            }
            this.model[field] = [...this.model[field], val];
            el.value = "";
            el.focus();
        },
        removeRate(field, index) {
            const arr = [...this.model[field]];
            arr.splice(index, 1);
            this.model[field] = arr;
        },
        onSaveAddNew() {
            this.errors = new Errors();
            this.waiting_new = true;
            this.$store.dispatch("creditOperation/loanTypeUserDefinedRates/store", this.model)
                .then(response => { this.notice({ type: "success", text: response.message }); this.clearInput(); this.$emit("fetchData"); })
                .catch(error => { this.errors = new Errors(error.errors); this.notice({ type: "error", text: error.message }); })
                .finally(() => { this.waiting_new = false; });
        },
        onSave() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store.dispatch("creditOperation/loanTypeUserDefinedRates/store", this.model)
                .then(response => { this.notice({ type: "success", text: response.message }); this.$emit("cancel"); this.$emit("fetchData"); })
                .catch(error => { this.errors = new Errors(error.errors); this.notice({ type: "error", text: error.message }); })
                .finally(() => { this.waiting = false; });
        },
        onUpdate() {
            this.waiting = true;
            this.$store.dispatch("creditOperation/loanTypeUserDefinedRates/update", { id: this.edit_data.rate_id, data: this.model })
                .then(response => { this.notice({ type: "success", text: response.message }); this.$emit("cancel"); this.$emit("fetchData"); })
                .catch(error => { this.errors = new Errors(error.errors); this.notice({ type: "error", text: error.message }); })
                .finally(() => { this.waiting = false; });
        },
        setEditData() {
            if (!isEmpty(this.edit_data)) {
                this.model.loan_type_id   = this.edit_data.loan_type_id;
                this.model.interest_rates = [...(this.edit_data.interest_rates || [])];
                this.model.monthly_fees   = [...(this.edit_data.monthly_fees   || [])];
                this.model.admin_fees     = [...(this.edit_data.admin_fees     || [])];
                this.model.insurance_fees = [...(this.edit_data.insurance_fees || [])];
                this.model.rescue_fees    = [...(this.edit_data.rescue_fees    || [])];
            }
        },
        clearInput() {
            this.irRangeError = null;
            this.model.loan_type_id   = null;
            this.model.interest_rates = [];
            this.model.monthly_fees   = [];
            this.model.admin_fees     = [];
            this.model.insurance_fees = [];
            this.model.rescue_fees    = [];
            RATE_FIELDS.forEach(({ ref }) => { if (this.$refs[ref]) this.$refs[ref].value = ""; });
        },
        notice(not) {
            this.$Notice[not.type]({ title: "LOAN TYPE USER DEFINED RATE", desc: not.text });
        }
    }
};
</script>

<style scoped>
.tag-box {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    min-height: 38px;
    padding: 4px 8px;
    border: 1px solid #dcdee2;
    border-radius: 4px;
    background: #fff;
}
.tag-box:focus-within { border-color: #57a3f3; box-shadow: 0 0 0 2px rgba(45,140,240,.2); }
.tag-box-error { border-color: #ed4014 !important; }
.tag-box-error:focus-within { box-shadow: 0 0 0 2px rgba(237,64,20,.2) !important; }
.tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 12px;
    color: #fff;
    user-select: none;
}
.chip-blue   { background: #2d8cf0; }
.chip-green  { background: #19be6b; }
.chip-gold   { background: #f5a623; }
.chip-cyan   { background: #00b5ff; }
.chip-purple { background: #9c27b0; }
.tag-chip em {
    font-style: normal;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    opacity: .8;
}
.tag-chip em:hover { opacity: 1; }
.tag-field {
    flex: 1;
    min-width: 80px;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    padding: 2px 0;
    color: #515a6e;
}
.tag-field::-webkit-outer-spin-button,
.tag-field::-webkit-inner-spin-button { -webkit-appearance: none; }
.tag-add-btn {
    padding: 1px 8px;
    border: 1px solid #dcdee2;
    border-radius: 3px;
    background: #f7f7f7;
    font-size: 16px;
    line-height: 1.4;
    cursor: pointer;
    color: #515a6e;
    flex-shrink: 0;
}
.tag-add-btn:hover { background: #e6f7ff; border-color: #57a3f3; color: #2d8cf0; }
</style>
